// Floating element control buttons (add / move up / move down / duplicate / delete)
// shown when hovering an inline-editable element in OfferPageBuilderSimple.
// Extracted verbatim from the inner ElementControls sub-component of
// renderInlineEditableElement so the per-element-type cases can later be split
// into their own files. Props expose the dependencies that the original closure
// captured.

import React from 'react';
import { Box, IconButton } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as MoveUpIcon,
  KeyboardArrowDown as MoveDownIcon,
  FileCopy as DuplicateIcon,
} from '@mui/icons-material';

/**
 * @param {object} props
 * @param {object} props.element - the element being edited
 * @param {boolean} [props.isInZone=false]
 * @param {string|null} [props.layoutId=null]
 * @param {string|null} [props.zoneId=null]
 * @param {boolean} props.isBrandsPage
 * @param {boolean} props.paginationEnabled
 * @param {number} props.currentPageIndex
 * @param {(id: string|null) => void} props.setInsertAfterElementId
 * @param {(n: number) => void} props.setTabValue
 * @param {(b: boolean) => void} props.setRightPanelOpen
 * @param {(updater: Function) => void} props.setPages
 * @param {(updater: Function) => void} props.setElements
 * @param {Function} props.findAndUpdateLayout
 * @param {(id: string, dir: 'up'|'down') => void} props.moveElement
 * @param {() => string} props.generateId
 * @param {(id: string) => void} props.deleteElement
 * @param {(id: string, layoutId: string, zoneId: string) => void} props.deleteElementFromZone
 */
const ElementControls = ({
  element,
  isInZone = false,
  layoutId = null,
  zoneId = null,
  isBrandsPage,
  paginationEnabled,
  currentPageIndex,
  setInsertAfterElementId,
  setTabValue,
  setRightPanelOpen,
  setPages,
  setElements,
  findAndUpdateLayout,
  moveElement,
  generateId,
  deleteElement,
  deleteElementFromZone,
}) => (
  <Box
    className="element-controls"
    sx={{
      position: 'absolute',
      top: -8,
      right: -8,
      opacity: 0,
      transition: 'opacity 0.2s',
      display: 'flex',
      gap: 0.5,
      zIndex: 10,
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '4px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
    }}
  >
    {isBrandsPage && (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          // Store the current element ID so we can insert after it
          setInsertAfterElementId(element.id);
          setTabValue(2); // Go to "Add new element" tab
          setRightPanelOpen(true);
        }}
        title="Add new element"
        sx={{
          width: 24,
          height: 24,
          backgroundColor: '#4caf50',
          color: 'white',
          '&:hover': { backgroundColor: '#388e3c' },
          mr: 0.5
        }}
      >
        <AddIcon sx={{ fontSize: 14 }} />
      </IconButton>
    )}
    {/* Move Up control (replaces drag handle) */}
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        if (isInZone && layoutId && zoneId) {
          // Move element up within the zone
          if (paginationEnabled) {
            setPages(prev =>
              prev.map(page => ({
                ...page,
                elements: findAndUpdateLayout(page.elements, layoutId, zoneId, (zone) => {
                  const idx = zone.elements.findIndex(el => el.id === element.id);
                  if (idx <= 0) return zone;
                  const newZoneElements = [...zone.elements];
                  [newZoneElements[idx - 1], newZoneElements[idx]] = [newZoneElements[idx], newZoneElements[idx - 1]];
                  return { ...zone, elements: newZoneElements };
                })
              }))
            );
          } else {
            setElements(prev =>
              findAndUpdateLayout(prev, layoutId, zoneId, (zone) => {
                const idx = zone.elements.findIndex(el => el.id === element.id);
                if (idx <= 0) return zone;
                const newZoneElements = [...zone.elements];
                [newZoneElements[idx - 1], newZoneElements[idx]] = [newZoneElements[idx], newZoneElements[idx - 1]];
                return { ...zone, elements: newZoneElements };
              })
            );
          }
        } else {
          // Move element up on main canvas
          moveElement(element.id, 'up');
        }
      }}
      title="Move Up"
      sx={{
        width: 24,
        height: 24,
        backgroundColor: '#2630f3',
        color: 'white',
        '&:hover': { backgroundColor: '#1976d2' }
      }}
    >
      <MoveUpIcon sx={{ fontSize: 14 }} />
    </IconButton>

    {/* Move Down control */}
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        if (isInZone && layoutId && zoneId) {
          // Move element down within the zone
          if (paginationEnabled) {
            setPages(prev =>
              prev.map(page => ({
                ...page,
                elements: findAndUpdateLayout(page.elements, layoutId, zoneId, (zone) => {
                  const idx = zone.elements.findIndex(el => el.id === element.id);
                  if (idx < 0 || idx >= zone.elements.length - 1) return zone;
                  const newZoneElements = [...zone.elements];
                  [newZoneElements[idx], newZoneElements[idx + 1]] = [newZoneElements[idx + 1], newZoneElements[idx]];
                  return { ...zone, elements: newZoneElements };
                })
              }))
            );
          } else {
            setElements(prev =>
              findAndUpdateLayout(prev, layoutId, zoneId, (zone) => {
                const idx = zone.elements.findIndex(el => el.id === element.id);
                if (idx < 0 || idx >= zone.elements.length - 1) return zone;
                const newZoneElements = [...zone.elements];
                [newZoneElements[idx], newZoneElements[idx + 1]] = [newZoneElements[idx + 1], newZoneElements[idx]];
                return { ...zone, elements: newZoneElements };
              })
            );
          }
        } else {
          // Move element down on main canvas
          moveElement(element.id, 'down');
        }
      }}
      title="Move Down"
      sx={{
        width: 24,
        height: 24,
        backgroundColor: '#2630f3',
        color: 'white',
        '&:hover': { backgroundColor: '#1976d2' }
      }}
    >
      <MoveDownIcon sx={{ fontSize: 14 }} />
    </IconButton>

    {/* Duplicate Button */}
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        console.log('📋 Duplicate button clicked for element:', element.id);

        // Create a duplicate of the current element
        const duplicatedElement = {
          ...element,
          id: generateId(),
          // Add "(copy)" to text-based content if it exists
          content: element.content ? `${element.content} (copy)` : element.content,
          title: element.title ? `${element.title} (copy)` : element.title,
          // For custom lists, duplicate the rows with "(copy)" suffix
          rows: element.rows ? element.rows.map(row => ({
            ...row,
            text: `${row.text} (copy)`
          })) : element.rows,
          // For other elements with text arrays, duplicate with "(copy)" suffix
          items: element.items ? element.items.map(item => ({
            ...item,
            question: item.question ? `${item.question} (copy)` : item.question,
            answer: item.answer ? `${item.answer} (copy)` : item.answer
          })) : element.items,
          steps: element.steps ? element.steps.map(step => ({
            ...step,
            title: step.title ? `${step.title} (copy)` : step.title,
            description: step.description ? `${step.description} (copy)` : step.description
          })) : element.steps
        };

        // Insert the duplicated element below the current one
        if (isInZone && layoutId && zoneId) {
          // For elements in zones, insert into the same zone
          console.log('📋 Duplicating element in zone:', { layoutId, zoneId });

          // Add duplicated element to current page zone (always enabled now)
          setPages(prev => prev.map((page, pageIndex) => {
            if (pageIndex === currentPageIndex) {
              const updatedElements = findAndUpdateLayout(page.elements, layoutId, zoneId, (zone) => {
                const currentIndex = zone.elements.findIndex(el => el.id === element.id);
                const insertIndex = currentIndex + 1;
                const newZoneElements = [...zone.elements];
                newZoneElements.splice(insertIndex, 0, duplicatedElement);
                return { ...zone, elements: newZoneElements };
              });
              return { ...page, elements: updatedElements };
            }
            return page;
          }));

          // Also update flat elements list for consistency
          setElements(prev =>
            findAndUpdateLayout(prev, layoutId, zoneId, (zone) => {
              const currentIndex = zone.elements.findIndex(el => el.id === element.id);
              const insertIndex = currentIndex + 1;
              const newZoneElements = [...zone.elements];
              newZoneElements.splice(insertIndex, 0, duplicatedElement);
              return { ...zone, elements: newZoneElements };
            })
          );
        } else {
          // For elements in main canvas, insert below current element
          console.log('📋 Duplicating element in main list');

          // Add duplicated element to current page
          setPages(prevPages => {
            const safeIndex = Math.min(currentPageIndex, prevPages.length - 1);
            const currentPage = prevPages[safeIndex];
            const originalIndex = currentPage.elements.findIndex(el => el.id === element.id);

            const newElementsArray = [...currentPage.elements];
            newElementsArray.splice(originalIndex + 1, 0, duplicatedElement);

            return prevPages.map((page, pIdx) =>
              pIdx === safeIndex ? { ...page, elements: newElementsArray } : page
            );
          });

          // Also update flat elements list
          setElements(prev => {
            const flatIndex = prev.findIndex(el => el.id === element.id);
            const newElements = [...prev];
            // Handle case where element might not be in flat list (though it should be)
            if (flatIndex !== -1) {
              newElements.splice(flatIndex + 1, 0, duplicatedElement);
            } else {
              newElements.push(duplicatedElement);
            }
            return newElements;
          });
        }
      }}
      title="Duplicate element"
      sx={{
        width: 24,
        height: 24,
        backgroundColor: '#4caf50',
        color: 'white',
        '&:hover': { backgroundColor: '#388e3c' }
      }}
    >
      <DuplicateIcon sx={{ fontSize: 12 }} />
    </IconButton>

    {/* Delete Button */}
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        console.log('🗑️ Delete button clicked for element:', element.id);
        console.log('🗑️ Delete context:', { isInZone, layoutId, zoneId });
        console.log('🗑️ Available functions:', {
          hasDeleteElementFromZone: typeof deleteElementFromZone === 'function',
          hasDeleteElement: typeof deleteElement === 'function'
        });

        if (isInZone && layoutId && zoneId) {
          console.log('🗑️ Calling deleteElementFromZone...');
          deleteElementFromZone(element.id, layoutId, zoneId);
        } else {
          console.log('🗑️ Calling deleteElement...');
          deleteElement(element.id);
        }
      }}
      title="Delete element"
      sx={{
        width: 24,
        height: 24,
        backgroundColor: '#f44336',
        color: 'white',
        '&:hover': { backgroundColor: '#d32f2f' }
      }}
    >
      <DeleteIcon sx={{ fontSize: 12 }} />
    </IconButton>
  </Box>
);

export default ElementControls;
